import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RotateCcw, Loader2, ArrowLeft, ShieldCheck, Filter } from "lucide-react";
import { api, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "pending",  label: "Pending",  tone: "text-marigold border-marigold" },
  { key: "approved", label: "Approved", tone: "text-teal border-teal" },
  { key: "rejected", label: "Rejected", tone: "text-ink2 border-ink2" },
  { key: "all",      label: "All",      tone: "text-ink border-ink" },
];

export default function AdminTestimonials() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [busy, setBusy] = useState({}); // per-row action-in-flight

  const isAdmin = useMemo(() => {
    const e = (user?.email || "").toLowerCase();
    return e.endsWith("@durgaprogrammingadventures.com");
  }, [user]);

  const load = async (status = tab) => {
    setRows(null);
    try {
      const { data } = await api.get(`/admin/testimonials?status=${status}`);
      setRows(data.testimonials || []);
      setCounts(data.counts || {});
    } catch (e) {
      if (e?.response?.status === 403) {
        toast.error("Admin domain required.");
        nav("/");
      } else {
        toast.error("Could not load testimonials");
        setRows([]);
      }
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (!isAdmin) { toast.error("Admin only"); nav("/"); return; }
    load(tab);
  }, [user, loading, isAdmin, tab]);

  const moderate = async (id, status) => {
    setBusy((b) => ({ ...b, [id]: status }));
    try {
      await api.post(`/testimonials/${id}/moderate?status=${status}`);
      toast.success(`Marked ${status}`);
      load(tab);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moderation failed");
    } finally {
      setBusy((b) => { const c = { ...b }; delete c[id]; return c; });
    }
  };

  if (loading) return <div className="pt-32 text-center text-ink2"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>;
  if (!user || !isAdmin) return null;

  return (
    <div className="container-narrow py-10 md:py-14" data-testid="admin-testimonials-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <Link to="/admin" className="btn-ghost text-sm -ml-3 mb-2" data-testid="admin-testimonials-back">
            <ArrowLeft className="w-4 h-4" /> Admin home
          </Link>
          <div className="label-eyebrow mb-1 inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Moderation
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-semibold">Testimonial console</h1>
          <p className="text-ink2 text-sm mt-2 max-w-xl">Approve or reject creator stories. Approved ones show up on the marketing wall.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="admin-testimonials-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`admin-tab-${t.key}`}
            className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-1.5 text-xs uppercase tracking-widest font-bold transition ${
              tab === t.key
                ? `${t.tone} bg-cream shadow-[3px_3px_0_0_#1A1B41]`
                : "border-ink2/30 text-ink2 hover:border-ink2"
            }`}
          >
            <Filter className="w-3 h-3" />
            {t.label}
            <span className="text-ink2 font-mono normal-case">{counts[t.key] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Rows */}
      {rows === null ? (
        <div className="text-center py-12 text-ink2" data-testid="admin-testimonials-loading"><Loader2 className="w-5 h-5 mx-auto animate-spin" /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-ink2 text-sm" data-testid="admin-testimonials-empty">
          Nothing here yet in <em>{tab}</em>.
        </div>
      ) : (
        <ul className="space-y-4" data-testid="admin-testimonials-list">
          {rows.map((t) => (
            <li key={t.id} className="rounded-2xl border-2 border-ink bg-cream p-5 shadow-[4px_4px_0_0_#1A1B41]" data-testid={`admin-testimonial-${t.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1 text-[10px] uppercase tracking-widest font-bold">
                    <StatusPill status={t.status} />
                    <span className="text-ink2 font-mono normal-case">#{t.id.slice(0, 8)}</span>
                    <span className="text-ink2 font-mono normal-case">{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="font-hand text-xl text-marigold">{t.author_name}</div>
                  {t.role && <div className="text-[10px] uppercase tracking-widest font-bold text-ink2 mb-2">{t.role}</div>}
                  <p className="text-ink leading-relaxed text-sm mb-2">&ldquo;{t.body}&rdquo;</p>
                  {t.project_name && <div className="text-[10px] text-teal font-mono mt-1">→ {t.project_name}</div>}
                </div>
                {t.video_url && (
                  <video
                    src={`${process.env.REACT_APP_BACKEND_URL}${t.video_url}${t.status === "approved" ? "" : "/preview"}${t.status === "approved" ? "" : `?auth=${encodeURIComponent(localStorage.getItem("dpa_token") || "")}`}`}
                    controls
                    playsInline
                    preload="metadata"
                    className="w-64 aspect-video rounded-xl border-2 border-ink bg-ink object-cover"
                    data-testid={`admin-testimonial-video-${t.id}`}
                  />
                )}
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {t.status !== "approved" && (
                  <button
                    disabled={!!busy[t.id]}
                    onClick={() => moderate(t.id, "approved")}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-teal bg-teal text-cream px-4 py-1.5 text-xs uppercase tracking-widest font-bold hover:opacity-90 transition disabled:opacity-60"
                    data-testid={`admin-approve-${t.id}`}
                  >
                    {busy[t.id] === "approved" ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                    Approve
                  </button>
                )}
                {t.status !== "rejected" && (
                  <button
                    disabled={!!busy[t.id]}
                    onClick={() => moderate(t.id, "rejected")}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-ink px-4 py-1.5 text-xs uppercase tracking-widest font-bold hover:bg-ink hover:text-cream transition disabled:opacity-60"
                    data-testid={`admin-reject-${t.id}`}
                  >
                    {busy[t.id] === "rejected" ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    Reject
                  </button>
                )}
                {t.status !== "pending" && (
                  <button
                    disabled={!!busy[t.id]}
                    onClick={() => moderate(t.id, "pending")}
                    className="inline-flex items-center gap-1.5 rounded-full border-2 border-ink2/40 px-4 py-1.5 text-xs uppercase tracking-widest font-bold text-ink2 hover:border-ink2 transition disabled:opacity-60"
                    data-testid={`admin-repending-${t.id}`}
                  >
                    {busy[t.id] === "pending" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                    Move back to pending
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const style = {
    pending:  "bg-marigold/15 text-marigold border-marigold",
    approved: "bg-teal/15 text-teal border-teal",
    rejected: "bg-ink2/10 text-ink2 border-ink2",
  }[status] || "border-ink";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 ${style}`}>
      {status}
    </span>
  );
}
